import { useEffect, useState } from "react";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import { HashLoader } from "react-spinners";
import QRCode from "react-qr-code";
import { MainButton } from "../../components/MainButton";

import { dateForHumans } from "../../utils/dateForHumans";
import timeAgo from "../../utils/timeAgo";
import { ICONS } from "../../utils/icons";
import { COLORS } from "../../utils/colors";
import { getTicketTransactions } from "../../api/ticketTransactions";
import { getCustomer } from "../../api/customers";
import { deleteTicket } from "../../api/tickets";

export const TicketDetailsDrawer = ({
  open,
  setOpen,
  ticket,
  userData,
  handleSnackbar,
  fetchData,
  setNumberOfTickets,
}) => {
  const [transactions, setTransactions] = useState([]);
  const [senders, setSenders] = useState([]);
  const [receivers, setReceivers] = useState([]);
  const [loading, setLoading] = useState({
    main: false,
    sender: false,
    receiver: false,
    delete: false,
  });

  const handleClose = () => {
    setOpen(false);
  };

  const fields = [
    {
      label: "Ticket ID",
      value: "_id",
    },
    {
      label: "Event Name",
      value: "event_name",
    },
    {
      label: "Category Name",
      value: "category_name",
    },
    {
      label: "Ticket Type",
      value: "type",
    },
  ];

  useEffect(async () => {
    if (ticket) {
      try {
        setLoading({ ...loading, main: true });

        const { data, error } = await getTicketTransactions(
          ticket.transactions,
          userData.operators[0].api_key
        );

        if (data) {
          setTransactions(data);
          setLoading({ ...loading, main: false });

          data.forEach(async (item, index) => {
            if (item.sender) {
              const sender = await fetchCustomer(item.sender, "sender");

              setSenders((prev) => [...prev, { email: sender?.email, index }]);
            } else {
              setSenders((prev) => [...prev, { email: "-", index }]);
            }

            if (item.receiver) {
              const receiver = await fetchCustomer(item.receiver, "receiver");

              setReceivers((prev) => [
                ...prev,
                { email: receiver?.email, index },
              ]);
            } else {
              setReceivers((prev) => [...prev, { email: "-", index }]);
            }
          });
        }

        if (error) {
          console.log(error);
          handleSnackbar("Error getting ticket transactions", "error");
          setLoading({ ...loading, main: false });
        }
      } catch (error) {
        console.log(error);
        handleSnackbar("Error getting ticket transactions", "error");
        setLoading({ ...loading, main: false });
      }
    }
  }, [ticket]);

  const fetchCustomer = async (id, role) => {
    try {
      role === "sender" && setLoading({ ...loading, sender: true });
      role === "receiver" && setLoading({ ...loading, receiver: true });

      const { data, error } = await getCustomer({
        customerID: id,
        apiKey: userData.operators[0].api_key,
      });

      if (data) {
        if (role === "sender") setLoading({ ...loading, sender: false });

        if (role === "receiver") setLoading({ ...loading, receiver: false });

        return data;
      }

      if (error) {
        console.log(error);
        handleSnackbar(
          "Error getting ticket transactions sender/receiver",
          "error"
        );
        setLoading({ ...loading, sender: false });
      }
    } catch (error) {
      console.log(error);
      handleSnackbar(
        "Error getting ticket transactions sender/receiver",
        "error"
      );
      setLoading({ ...loading, sender: false });
    }
  };

  const handleDelete = async () => {
    try {
      const confirmAction = window.confirm(
        "Are you sure you want to take this action?"
      );

      if (!confirmAction) {
        setLoading(false);
        return;
      }

      setLoading({ ...loading, delete: true });

      const { data, error } = await deleteTicket({
        apiKey: userData.operators[0].api_key,
        ticketID: ticket._id,
      });

      if (data) {
        setOpen(false);
        fetchData();
        setNumberOfTickets((prev) => prev - 1);
      }

      if (error) {
        handleSnackbar(error, "error");
        setLoading({ ...loading, delete: false });
        return;
      }

      if (error) {
        handleSnackbar(error, "error");
        setLoading({ ...loading, delete: false });
        return;
      }

      setLoading({ ...loading, delete: false });
      handleClose();
      handleSnackbar("Discount deleted successfully", "success");
    } catch (error) {
      console.log("Ticket delete error: ", error);
      handleSnackbar(error, "error");
    }
  };

  const list = () => (
    <Box sx={{ width: 880, display: "flex", padding: 2 }} role="presentation">
      <div style={{ padding: 30 }}>
        <div
          className="container-row-full-width-space-between"
          style={{ margin: "0 0 20px" }}
        >
          <div>
            <h2 style={{ margin: "0" }}>Ticket Data</h2>
            <p>Analyze in detail the ticket data</p>
          </div>
          <MainButton
            disabled={loading.delete}
            color={COLORS.red}
            textColor={COLORS.white}
            textHoverColor={COLORS.lightRed}
            hoverColor={COLORS.red}
            shadowColor={COLORS.red}
            textAlignment={"center"}
            onClickAction={() => handleDelete()}
            width={"fit-content"}
          >
            {loading.delete ? (
              <>
                <HashLoader size={20} color={COLORS.grey} /> Delete
              </>
            ) : (
              "Delete"
            )}
          </MainButton>
        </div>
        {loading.main ? (
          <HashLoader color={COLORS.black} size={20} />
        ) : (
          <div
            style={{
              border: `1px solid ${COLORS.lightGrey}`,
              padding: 30,
              borderRadius: 6,
              width: "770px",
              wordBreak: "break-all",
            }}
          >
            {fields.map((field) => {
              return (
                <div
                  key={field.value}
                  className="container-column"
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    width: "-webkit-fill-available",
                    marginBottom: "20px",
                    paddingBottom: "15px",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  <p
                    id="subheader"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      marginBottom: "3px",
                    }}
                  >
                    {field.label}
                  </p>
                  {field.value !== "createdAt" && (
                    <p id="subheaderBlack" style={{ marginBottom: "5px" }}>
                      {ticket[field.value]}
                    </p>
                  )}
                </div>
              );
            })}
            <div
              className="container-column"
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                width: "-webkit-fill-available",
                marginBottom: "20px",
                paddingBottom: "15px",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <p
                id="subheader"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  marginBottom: "3px",
                }}
              >
                Customer
              </p>
              <p id="subheaderBlack" style={{ marginBottom: "5px" }}>
                {ticket.customer.email}
              </p>
            </div>
            <div
              className="container-column"
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                width: "-webkit-fill-available",
                marginBottom: "20px",
                paddingBottom: "15px",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <p
                id="subheader"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  marginBottom: "3px",
                }}
              >
                Listing Price
              </p>
              <p id="subheaderBlack" style={{ marginBottom: "5px" }}>
                {ticket?.last_price?.amount} {ticket?.last_price?.currency}
              </p>
            </div>
            {ticket.pwa_link && (
              <div
                className="container-column"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  width: "-webkit-fill-available",
                  marginBottom: "20px",
                  paddingBottom: "15px",
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <p
                  id="subheader"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    marginBottom: "3px",
                  }}
                >
                  Link to the LiveSync Ticket
                </p>
                <a
                  href={ticket.pwa_link}
                  target="_blank"
                  id="subheaderBlack"
                  style={{ marginBottom: "5px" }}
                >
                  LiveSync link
                </a>
              </div>
            )}
            {ticket.qr_type && (
              <div
                className="container-column"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  width: "-webkit-fill-available",
                  marginBottom: "20px",
                  paddingBottom: "15px",
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <p
                  id="subheader"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    marginBottom: "3px",
                  }}
                >
                  QR Code Type
                </p>
                <p id="subheaderBlack" style={{ marginBottom: "5px" }}>
                  {ticket.qr_type}
                </p>
              </div>
            )}
            {ticket.qr_hash && (
              <div
                className="container-column"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  width: "-webkit-fill-available",
                  marginBottom: "20px",
                  paddingBottom: "15px",
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <p
                  id="subheader"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    marginBottom: "3px",
                  }}
                >
                  QR Code
                </p>
                <div
                  style={{
                    // height: "auto",
                    maxWidth: 94,
                    width: "100%",
                  }}
                >
                  <QRCode
                    // size={256}
                    style={{ height: "auto", width: "100%" }}
                    value={ticket.qr_hash}
                    // viewBox={`0 0 356 356`}
                  />
                </div>
              </div>
            )}
            {ticket.smart_contract_address && (
              <div
                className="container-column"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  width: "-webkit-fill-available",
                  marginBottom: "20px",
                  paddingBottom: "15px",
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <p
                  id="subheader"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    marginBottom: "3px",
                  }}
                >
                  Smart Contract
                </p>
                <p id="subheaderBlack" style={{ marginBottom: "5px" }}>
                  {ticket.smart_contract_address}
                </p>
              </div>
            )}
            <div
              className="container-column"
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "-webkit-fill-available",
                marginBottom: "20px",
                paddingBottom: "15px",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <p
                id="subheader"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  marginBottom: "5px",
                }}
              >
                Transactions
              </p>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontFamily: "AuthenticSans90" }}>
                      Date
                    </TableCell>
                    <TableCell sx={{ fontFamily: "AuthenticSans90" }}>
                      Type
                    </TableCell>
                    <TableCell sx={{ fontFamily: "AuthenticSans90" }}>
                      Sender
                    </TableCell>
                    <TableCell sx={{ fontFamily: "AuthenticSans90" }}>
                      Receiver
                    </TableCell>
                    <TableCell sx={{ fontFamily: "AuthenticSans90" }}>
                      Price
                    </TableCell>
                    <TableCell sx={{ fontFamily: "AuthenticSans90" }}>
                      Status
                    </TableCell>
                    {ticket.smart_contract_address && (
                      <TableCell sx={{ fontFamily: "AuthenticSans90" }}>
                        Tx
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions?.map((item, index) => {
                    return (
                      <TableRow key={index}>
                        <TableCell sx={{ fontFamily: "AuthenticSans90" }}>
                          {dateForHumans(item.createdAt)}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "AuthenticSans90" }}>
                          {item.type}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "AuthenticSans90" }}>
                          {loading.sender ? (
                            <HashLoader color={COLORS.black} size={15} />
                          ) : (
                            senders[index] &&
                            senders.find((obj) => obj.index === index)?.email
                          )}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "AuthenticSans90" }}>
                          {loading.receiver ? (
                            <HashLoader color={COLORS.black} size={15} />
                          ) : (
                            receivers[index] &&
                            receivers.find((obj) => obj.index === index)?.email
                          )}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "AuthenticSans90" }}>
                          {item.price
                            ? `${item?.price} ${ticket.original_price?.currency}`
                            : "-"}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "AuthenticSans90" }}>
                          {item.status}
                        </TableCell>
                        {ticket.smart_contract_address && (
                          <TableCell
                            sx={{
                              fontFamily: "AuthenticSans90",
                              fontWeight: "bold",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              item.tx_hash &&
                              window.open(
                                `${process.env.REACT_APP_POLYGONSCAN}/tx/${item.tx_hash}`
                              )
                            }
                          >
                            {item.tx_hash ? ICONS.external20 : "-"}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <div
              className="container-column"
              style={{
                display: "flex",
                // flexDirection: "row",
                justifyContent: "space-between",
                width: "-webkit-fill-available",
                marginBottom: "20px",
              }}
            >
              <p
                id="subheader"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  marginBottom: "3px",
                }}
              >
                Ticket History
              </p>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontFamily: "AuthenticSans90" }}>
                      When
                    </TableCell>
                    <TableCell sx={{ fontFamily: "AuthenticSans90" }}>
                      Date
                    </TableCell>
                    <TableCell sx={{ fontFamily: "AuthenticSans90" }}>
                      Message
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ticket.history.general?.map((item, index) => {
                    return (
                      <TableRow key={index}>
                        <TableCell
                          sx={{
                            fontFamily: "AuthenticSans90",
                            fontWeight: "bold",
                          }}
                        >
                          {timeAgo(item.date)}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "AuthenticSans90",
                            fontWeight: "bold",
                          }}
                        >
                          {dateForHumans(item.date)}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "AuthenticSans90",
                            fontWeight: "bold",
                          }}
                        >
                          {item.message}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <div
              className="container-column"
              style={{
                display: "flex",
                // flexDirection: "row",
                justifyContent: "space-between",
                width: "-webkit-fill-available",
                marginBottom: "20px",
              }}
            >
              <p
                id="subheader"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  marginBottom: "3px",
                }}
              >
                Scan History
              </p>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontFamily: "AuthenticSans90" }}>
                      When
                    </TableCell>
                    <TableCell sx={{ fontFamily: "AuthenticSans90" }}>
                      Date
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ticket.history.scan_history?.map((item, index) => {
                    return (
                      <TableRow key={index}>
                        <TableCell
                          sx={{
                            fontFamily: "AuthenticSans90",
                            fontWeight: "bold",
                          }}
                        >
                          {timeAgo(item.date)}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "AuthenticSans90",
                            fontWeight: "bold",
                          }}
                        >
                          {dateForHumans(item.date)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </Box>
  );

  return (
    <Drawer anchor={"right"} open={open} onClose={() => handleClose()}>
      {list("right")}
    </Drawer>
  );
};
